/**
 * Main Application Component
 * 
 * Sets up routing, handles global authentication redirect checking,
 * and renders the layout and modal dialogs using centralized
 * state from AppContext and AuthContext.
 */

import React, { useEffect, useRef } from 'react';
import { Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import './App.css';

// Pages
import Login from './pages/Login';
import Signup from './pages/Signup';
import Profile from './pages/Profile';
import UpdateProfile from './pages/UpdateProfile';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import TermsOfService from './pages/TermsOfService';
import Privacy from './pages/Privacy';
import Security from './pages/Security';
import Checkout from './pages/Checkout';
import FollowUp from './pages/FollowUp';

// Components
import PrivateRoute from './components/PrivateRoute';
import ReviewDisplay from './components/ReviewDisplay';
import RewritePopup from './components/RewritePopup';
import QuickDraftModal from './components/QuickDraftModal';
import AppNavbar from './components/AppNavbar';
import EditorSection from './components/EditorSection';
import SuggestionsSection from './components/SuggestionsSection';

// Hooks
import { useAppContext } from './contexts/AppContext';
import { useAuth } from './contexts/AuthContext';
import { useAppHandlers } from './hooks/useAppHandlers';
import { useRewriteFeature } from './hooks/useRewriteFeature';

// HeroUI Components
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Spinner
} from '@heroui/react';
import {
  PencilIcon,
  ChatBubbleLeftRightIcon,
  ScaleIcon
} from '@heroicons/react/24/outline';

function App() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  // Zentralized State and Actions
  const { state, actions } = useAppContext();
  const { 
    theme, 
    supervisorLoading, 
    lawyerLoading, 
    supervisorReview, 
    lawyerReview, 
    contactFormStatus, 
    contactFormLoading,
    selectedText,
    showRewriteButton,
    buttonPosition,
    isDrafting
  } = state;

  const isSupervisorModalOpen = state.modals.supervisor;
  const isLawyerModalOpen = state.modals.lawyer;
  const isContactModalOpen = state.modals.contact;
  const isQuickDraftModalOpen = state.modals.quickDraft;
  const isRewritePopupOpen = state.modals.rewrite;

  const { 
    setModalState,
    setContactFormStatus,
    setContactFormLoading
  } = actions;

  // Reference for the editor textarea
  const textareaRef = useRef(null);

  // Hook-based handlers
  const {
    generateSupervisorReview,
    generateLawyerReview,
    handleGenerateDraft
  } = useAppHandlers();

  const {
    handleOpenRewritePopup,
    handleApplyRewrite,
    getContextForRewrite
  } = useRewriteFeature(textareaRef);

  // Theme Sync Effect
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  // Firebase Auth Redirect Interceptor
  useEffect(() => {
    const checkForFirebaseAuthRedirect = () => {
      const url = window.location.href;
      if (url.includes('/__/auth/') || 
          url.includes('mode=action') || 
          url.includes('mode=resetPassword')) {
        console.log('Firebase auth redirect detected, navigating to login');
        navigate('/login');
        return true;
      }
      return false;
    };

    checkForFirebaseAuthRedirect();
    const interval = setInterval(checkForFirebaseAuthRedirect, 500);
    return () => clearInterval(interval);
  }, [navigate]);

  // API base URL finder
  const getApiBaseUrl = () => {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:3001';
    } else if (hostname === 'newreports.apexionx.com') {
      return 'https://reportsnew.apexionx.com';
    } else {
      return window.location.origin;
    }
  };

  // Contact support form handler
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactFormLoading(true);
    setContactFormStatus('');
    
    const formData = {
      name: e.target.name.value,
      email: e.target.email.value,
      subject: e.target.subject.value,
      message: e.target.message.value
    };
    
    try {
      const apiBase = getApiBaseUrl();
      const endpoint = apiBase.endsWith('/api') ? '/send-contact-email' : '/api/send-contact-email';
      const apiUrl = `${apiBase}${endpoint}`;
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setContactFormStatus('success');
        e.target.reset();
      } else {
        setContactFormStatus(`error: ${data.message}`);
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setContactFormStatus('error: Network error. Please try again.');
    } finally {
      setContactFormLoading(false);
    }
  };

  return (
    <div>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/security" element={<Security />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
        <Route path="/update-profile" element={<PrivateRoute><UpdateProfile /></PrivateRoute>} />
        <Route path="/follow-up" element={<PrivateRoute><FollowUp /></PrivateRoute>} />
        <Route
          path="/"
          element={
            currentUser ? (
              <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-blue-900">
                <AppNavbar />
                
                <div className="flex h-[calc(100vh-64px)]">
                  <EditorSection textareaRef={textareaRef} />
                  <SuggestionsSection />
                </div>

                {/* Review Modals */}
                <Modal 
                  isOpen={isSupervisorModalOpen} 
                  onOpenChange={(isOpen) => setModalState('supervisor', isOpen)}
                  size="4xl"
                  scrollBehavior="inside"
                >
                  <ModalContent>
                    {(onClose) => (
                      <>
                        <ModalHeader className="flex gap-2 items-center">
                          <ChatBubbleLeftRightIcon className="h-5 w-5 text-secondary" />
                          AI Supervisor Review
                        </ModalHeader>
                        <ModalBody>
                          {supervisorLoading ? (
                            <div className="flex justify-center items-center py-8">
                              <Spinner size="lg" color="secondary" />
                            </div>
                          ) : (
                            <ReviewDisplay htmlContent={supervisorReview} />
                          )}
                        </ModalBody>
                        <ModalFooter>
                          <Button color="default" variant="light" onPress={onClose}>
                            Close
                          </Button>
                          <Button color="secondary" onPress={generateSupervisorReview} isLoading={supervisorLoading}>
                            Regenerate
                          </Button>
                        </ModalFooter>
                      </>
                    )}
                  </ModalContent>
                </Modal>

                <Modal 
                  isOpen={isLawyerModalOpen} 
                  onOpenChange={(isOpen) => setModalState('lawyer', isOpen)}
                  size="4xl"
                  scrollBehavior="inside"
                >
                  <ModalContent>
                    {(onClose) => (
                      <>
                        <ModalHeader className="flex gap-2 items-center">
                          <ScaleIcon className="h-5 w-5 text-warning" />
                          AI Legal Review
                        </ModalHeader>
                        <ModalBody>
                          {lawyerLoading ? (
                            <div className="flex justify-center items-center py-8">
                              <Spinner size="lg" color="warning" />
                            </div>
                          ) : (
                            <ReviewDisplay htmlContent={lawyerReview} />
                          )}
                        </ModalBody>
                        <ModalFooter>
                          <Button color="default" variant="light" onPress={onClose}>
                            Close
                          </Button>
                          <Button color="warning" onPress={generateLawyerReview} isLoading={lawyerLoading}>
                            Regenerate
                          </Button>
                        </ModalFooter>
                      </>
                    )}
                  </ModalContent>
                </Modal>

                {/* Contact Modal */}
                <Modal isOpen={isContactModalOpen} onOpenChange={(isOpen) => setModalState('contact', isOpen)} size="lg">
                  <ModalContent>
                    {(onClose) => (
                      <>
                        <ModalHeader>Contact Support</ModalHeader>
                        <form onSubmit={handleContactSubmit}>
                          <ModalBody className="gap-4">
                            <Input name="name" label="Name" variant="bordered" required />
                            <Input name="email" label="Email" type="email" variant="bordered" required />
                            <Input name="subject" label="Subject" variant="bordered" required />
                            <textarea
                              name="message"
                              placeholder="Your message..."
                              className="w-full p-3 border border-gray-300 rounded-lg min-h-[100px] resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                              required
                            />
                            {contactFormStatus && (
                              <div className={`text-sm p-2 rounded ${
                                contactFormStatus === 'success' 
                                  ? 'text-green-600 bg-green-50' 
                                  : 'text-red-600 bg-red-50'
                              }`}>
                                {contactFormStatus === 'success' 
                                  ? 'Message sent successfully!' 
                                  : contactFormStatus}
                              </div>
                            )}
                          </ModalBody>
                          <ModalFooter>
                            <Button color="danger" variant="light" onPress={onClose}>
                              Cancel
                            </Button>
                            <Button 
                              color="primary" 
                              type="submit" 
                              isLoading={contactFormLoading}
                            >
                              Send Message
                            </Button>
                          </ModalFooter>
                        </form>
                      </>
                    )}
                  </ModalContent>
                </Modal>
              </div>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
      </Routes>

      {/* Global Modals and Components */}
      <RewritePopup
        isOpen={isRewritePopupOpen}
        onClose={() => setModalState('rewrite', false)}
        selectedText={selectedText}
        onApplyRewrite={handleApplyRewrite}
        context={getContextForRewrite()}
      />

      {showRewriteButton && (
        <div
          className="fixed z-50 pointer-events-auto"
          style={{
            left: `${buttonPosition.x}px`,
            top: `${buttonPosition.y}px`,
          }}
        >
          <Button
            size="sm"
            color="primary"
            variant="shadow"
            startContent={<PencilIcon className="h-3 w-3" />}
            onPress={handleOpenRewritePopup}
            className="text-xs font-medium shadow-lg hover:shadow-xl transition-all duration-200"
          >
            Rewrite
          </Button>
        </div>
      )}

      <QuickDraftModal
        isOpen={isQuickDraftModalOpen}
        onClose={() => setModalState('quickDraft', false)}
        onGenerateDraft={handleGenerateDraft}
        isDrafting={isDrafting}
      />
    </div>
  );
}

export default App;